import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { phone } from 'phone';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/app.config';
import { FormGroupStringType } from 'src/app/models/shared/shared-types';
import { InputFocusAbstractService } from 'src/app/services/input-focus/input-focus.abstract.service';
@Component({
    selector: 'appform-text-input',
    templateUrl: './text-input.component.html',
    styleUrls: ['./text-input.component.css'],
    standalone: false
})
export class TextInputComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() question_id: string;
  @Input() answer_id: string;
  @Input() code: string;
  @Input() fieldType: string;
  @Input() isautocomplete: boolean;
  @Input() text: string;
  @Input() label: string;
  @Input() error_text: string;
  @Input() help_text: string;
  @Input() required: boolean;
  @Input() pattern: string;
  @Input() placeholder: string;
  @Input() minlength: number;
  @Input() maxlength: number;
  @Input() min: number;
  @Input() max: number;
  @Input() value: string;
  @Input() invalid: boolean;
  @Input() hide_label: boolean;
  @Input() readOnly: boolean;
  @Input() mask: string;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onControlValueChanges = new EventEmitter<any>();
  @ViewChild('input') input: ElementRef<any>;

  private _formcontrolUnsub = new Subject<void>();

  get displayHelpText(): boolean {
    return this.help_text ? true : false;
  }
  public paramFormGroup: FormGroup<FormGroupStringType>;
  public inputControl: FormControl<string>;

  get isInvalid(): boolean {
    this.invalid = this.inputControl.invalid && this.inputControl.touched;
    return this.invalid;
  }
  constructor(
    private inputFocusService: InputFocusAbstractService,
    protected loggerService: LoggerAbstractService,
    protected logger: NGXLogger
  ) {}

  ngAfterViewInit(): void {
    this.inputFocusService.emitAgentInputIsSet(this.input);
    // This forces validation for prefilled fields
    if(this.inputControl?.value){
      this.inputControl.markAsDirty();
      this.inputControl.markAllAsTouched();
    }
  }

  ngOnInit(): void {
    this.initControlGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.value && this.value && this.inputControl) {
      this.inputControl.setValue(this.value);
    }
  }

  private initControlGroup() {
    const formControlsGroup: FormGroupStringType = {};
    this.paramFormGroup = new FormGroup<FormGroupStringType>(formControlsGroup);
    const validators = this.setUpValidators();
    this.inputControl = new FormControl<string>(null, validators);
    this.inputControl.setValue(this.value);
    this.paramFormGroup.addControl(this.question_id, this.inputControl);
    this.inputControl.valueChanges
      .pipe(
        debounceTime(300), //had to added debounceTime to avoid Redux Async Error from SDK. Will Re-evaluate after discussing with BP.
        takeUntil(this._formcontrolUnsub)
      )
      .subscribe(
        (value: any) => {
          const event = {
            type: 'onControlValueChanges',
            data: {
              question_id: this.question_id,
              answer_id: this.answer_id,
              code: this.code,
              field_type: this.fieldType,
              value: value,
              invalid: this.inputControl.status === 'INVALID'
            }
          };
          this.onControlValueChanges.emit(event);
        },
        (err: string) => {
          this.loggerService.postLogData(LogLevel.Error, 'text-input: initControlGroup', err, '')
          this._formcontrolUnsub.next();
          this._formcontrolUnsub.complete();
        }
      );
  }

  public getAriaLabel() {
    let labelBuilder = this.label;
    if (this.isInvalid) {
      labelBuilder += this.error_text;
    }
    return labelBuilder;
  }

  private setUpValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (this.required) {
      validators.push(Validators.required);
    }

    if (this.pattern !== null && this.fieldType === 'phone') {
      this.pattern = `[2-9]((?!11)[0-8]\\d)[2-9]((?!11)\\d{2})\\d{4}$`;
      validators.push(this.phoneValidator());
    } else {
      this.patternMatch(this.code);
    }

    if(this.code === 'progressive_commercial_business_name'){
      validators.push(this.specialCharValidator());
      validators.push(this.acronymValidator());
    }

     return validators;
  }

  private phoneValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const isPhoneValid = phone(control.value);
      if (isPhoneValid.isValid && isPhoneValid.countryIso2 === 'US' && AppConfig.settings.phoneValidation.restrictedAreaCode.indexOf(isPhoneValid.phoneNumber.substring(2,5)) <= -1) {
        return null;
      } else {
        return { invalidPhone: true };
      }
    };
  }

  private specialCharValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const specialChars =/^[`!@#$%^&*()_\-+=[\]{};':"\\|,.<>\\/?~ ]+$/;
      if(RegExp(specialChars).test(control.value)){
        return {invalidBusinessName: true};
      } else {
        return null;
      }

    };
  }

  private acronymValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const acronymsBlacklist = AppConfig.settings.businessNameValidation.restrictedBusinessNames;
      for (const acronym of acronymsBlacklist) {
        if (acronym.toUpperCase() === control.value?.toUpperCase()) {
          return { invalidBusinessName: true };
        }
      }
      return null;
    };
  }

  private patternMatch(incoming: string): void {
    switch (incoming) {
      case 'progressive_commercial_first_name':
        this.pattern = `^[^<>%\\|~*^\\\\_=\\{\\}\\[\\]]*$`;
        break;
      case 'progressive_commercial_middle_initial':
        this.pattern = `[A-Za-z]?`;
        break;
      case 'progressive_commercial_last_name':
        this.pattern = `^[^<>%\\|~*^\\\\_=\\{\\}\\[\\]]*$`;
        break;
      case 'progressive_commercial_email':
        this.pattern = `^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z0-9]{2,}$`;
        break;
      case 'progressive_commercial_business_name':
        this.pattern = `^[^<>%\\|~*^\\\\_=\\{\\}\\[\\]]*$`;
        break;
      case 'progressive_commercial_address_1':
        this.pattern = `^[^<>"%;:!\\)\\("+\\|~*?^$_=+\\{\\}\\[\\]]*$`;
        break;
      case 'progressive_commercial_address_2':
        this.pattern = `^[^<>"%;:!\\)\\("+\\|~*?^$_=+\\{\\}\\[\\]]*$`;
        break;
      case 'progressive_commercial_city':
        this.pattern = `^[^<>"%;:!\\)\\("+\\|~*?^$_=+\\{\\}\\[\\]]*$`;
        break;
      case 'progressive_commercial_zipcode':
        this.pattern = `^\\d{5}$`;
        break;
      default:
        this.pattern = null;
    }
  }

}
